<script setup lang="ts">
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import ActionBarBlock from '@/components/Interface/DataTable/ActionBar/ActionBarBlock.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import HeaderFixedWidthIcon from '@/components/Interface/DataTable/Headers/HeaderFixedWidthIcon.vue'
import HeaderFixedWidthTextNoSort from '@/components/Interface/DataTable/Headers/HeaderFixedWidthTextNoSort.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import { useBattleSimulatorStore } from '@/stores/battleSimulatorStore'
import { useGameStore } from '@/stores/gameStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const battleSimulatorStore = useBattleSimulatorStore()
const gameStore = useGameStore()

const openAddDesignModal = () => {
  gameStore.modalType = 'battleSimulatorAddAttackingDesign'
}

const openCopyFleetModal = () => {
  gameStore.modalType = 'battleSimulatorCopyAttackingFleet'
}
</script>

<template>
  <div>
    <div
      class="fleet-header rounded-t bg-cover bg-center flex items-start p-2 mb-1"
      :style="'background-image: url(' + gameStore.cdnUrl + 'images/interface/headers/fleet.webp)'"
    >
      <span class="text-xl text-white relative z-10">{{ t('command.battleSimulator.attackingFleet') }}</span>
    </div>
    <FerionDataTable
      :data="battleSimulatorStore.attackingFleet"
      tableStoreName="battleSimulatorAttacking"
      :selector="false"
      :footer-bar="false"
      heightLimit="h-auto"
      :noResultMessage="t('command.battleSimulator.noDesignsAdded')"
    >
      <template #actionBar>
        <ActionBarBlock class="mr-1 pl-2 flex items-center gap-2 rounded">
          <ActionButton @click="openAddDesignModal">{{ t('command.ships.addShips') }}</ActionButton>
          <ActionButton @click="openCopyFleetModal">{{ t('command.fleets.importFleet') }}</ActionButton>
        </ActionBarBlock>
        <ActionBarFiller class="rounded" />
      </template>

      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderTextFlex min-width="8" column="name" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <div class="pl-2">{{ t('command.shipDesigns.columns.name') }}</div>
        </HeaderTextFlex>
        <HeaderFixedWidthIcon width="5" column="hitpoints" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon type="hit_points" :tooltip="t('command.battleSimulator.tooltips.hitpoints')" />
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="5" column="attackVsShips" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon type="attack_against_ships" :tooltip="t('command.battleSimulator.tooltips.attackVsShips')" />
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="5" column="attackVsPlanet" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon type="attack_against_planets" :tooltip="t('command.battleSimulator.tooltips.attackVsPlanet')" />
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="5" column="shields" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon type="shields" :tooltip="t('command.shipDesigns.tooltips.shields')" />
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="5" column="quantity" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          {{ t('command.battleSimulator.quantity') }}
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthTextNoSort width="9" align="center">
          {{ t('command.shipTabs.actions') }}
        </HeaderFixedWidthTextNoSort>
      </template>

      <template #rows="{ item }">
        <CellFlexWidth min-width="8">
          <div class="pl-2">{{ item.name }}</div>
        </CellFlexWidth>
        <CellFixedWidth width="5">
          {{ item.hitpoints }}
        </CellFixedWidth>
        <CellFixedWidth width="5">
          {{ item.attackVsShips }}
        </CellFixedWidth>
        <CellFixedWidth width="5">
          {{ item.attackVsPlanet }}
        </CellFixedWidth>
        <CellFixedWidth width="5">
          {{ item.shields }}
        </CellFixedWidth>
        <CellFixedWidth width="5">
          {{ item.quantity }}
        </CellFixedWidth>
        <CellFixedWidth width="9" align="end">
          <div class="flex gap-1 pr-2">
            <ActionButton :for-icon="true" @click.stop="battleSimulatorStore.decreaseAttackingQuantity(item.id)">
              <GenericIcon type="remove" :tooltip="t('command.battleSimulator.tooltips.decreaseQuantity')" />
            </ActionButton>
            <ActionButton :for-icon="true" @click.stop="battleSimulatorStore.increaseAttackingQuantity(item.id)">
              <GenericIcon type="add" :tooltip="t('command.battleSimulator.tooltips.increaseQuantity')" />
            </ActionButton>
            <ActionButton :for-icon="true" @click.stop="battleSimulatorStore.removeAttackingShipDesign(item.id)">
              <GenericIcon type="trash" :tooltip="t('command.battleSimulator.tooltips.removeDesign')" />
            </ActionButton>
          </div>
        </CellFixedWidth>
      </template>
    </FerionDataTable>

    <!-- Totals Row -->
    <div v-if="battleSimulatorStore.attackingFleet.size > 0" class="flex h-10 bg-slate-700/40 text-slate-300 font-semibold items-center rounded-b">
      <div class="flex-1 min-w-32 pl-2">{{ t('command.battleSimulator.total') }}</div>
      <div class="w-20 flex-shrink-0 text-center">{{ battleSimulatorStore.attackingTotals.hitpoints }}</div>
      <div class="w-20 flex-shrink-0 text-center">{{ battleSimulatorStore.attackingTotals.attackVsShips }}</div>
      <div class="w-20 flex-shrink-0 text-center">{{ battleSimulatorStore.attackingTotals.attackVsPlanet }}</div>
      <div class="w-20 flex-shrink-0 text-center">{{ battleSimulatorStore.attackingTotals.shields }}</div>
      <div class="w-20 flex-shrink-0 text-center">{{ battleSimulatorStore.attackingTotals.quantity }}</div>
      <div class="w-36 flex-shrink-0"></div>
    </div>
  </div>
</template>

<style scoped>
:deep(.h-12.bg-slate-800) {
  background-color: transparent !important;
}

:deep(.h-12.cursor-pointer) {
  cursor: default !important;
}

.fleet-header {
  height: 8rem;
  position: relative;
}

.fleet-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6), transparent);
  border-radius: inherit;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  pointer-events: none;
}
</style>
